/**
 * @file Core navigation display logic for the getDirectionsFromTo plugin
 *
 * This module handles:
 * - Processing navigation requests (showDirectionsFromTo event)
 * - Route calculation and display
 * - Step-by-step navigation controls
 * - Map animation and navline rendering
 * - Security/immigration lane picker modal
 *
 * ## Event Flow
 *
 * ```
 * navigation/showDirectionsFromTo
 *         │
 *         ▼
 * ┌───────────────────┐
 * │ Resolve endpoints │ (wayfinder/getNavigationEndpoint)
 * └─────────┬─────────┘
 *           │
 *           ▼
 * ┌───────────────────┐     ┌──────────────────┐
 * │ Check route path  │────▶│ Show lane picker │ (if security/immigration)
 * └─────────┬─────────┘     └────────┬─────────┘
 *           │                        │
 *           ▼                        ▼
 * ┌───────────────────┐     ┌──────────────────┐
 * │ Calculate routes  │◀────│ Submit lanes     │
 * └─────────┬─────────┘     └──────────────────┘
 *           │
 *           ▼
 * ┌───────────────────┐
 * │ Display routes    │ (navlines, markers, steps)
 * └─────────┬─────────┘
 *           │
 *           ▼
 * ┌───────────────────┐
 * │ Step navigation   │ (forward/backward/stepTo)
 * └───────────────────┘
 * ```
 *
 * ## State Updates
 *
 * This module updates widgetState at these key points:
 * - After endpoint resolution: `endpoints`, `endpointIds`
 * - After route calculation: `directRoutes`, `accessibleRoutes`, `steps`, `segments`, etc.
 * - During navigation: `currentStep`, `currentStop`
 * - Lane picker: `securityLanes`, `immigrationLanes`, `lanePickerStep`
 *
 * @see ./types.js for type definitions
 * @see ./widgetState.js for state management
 */

import * as R from 'ramda';
import React from 'react';

import { findBoundsOfRoute, findBoundsOfSegments } from '@utils/bounds.ts';
import { getRouteDistance } from '@utils/distance.ts';
import { getBearing } from '@utils/geodesy.ts';

import { widgetEnum } from '../../../../utils/constants.ts';
import { SecurityLaneType as PickerSteps } from '../../../wayfinder/src/wayfinder.ts';
import {
  DIRECTIONS_RESULT_CONTROLS_ID,
  DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
  DIRECTIONS_RESULT_VIEW_ID_MOBILE,
  DIRECTIONS_SEARCH_VIEW_ID,
  LANE_PICKER_VIEW_ID,
  RouteOptionsClassification,
  RouteType,
} from './constants.ts';
import DirectionsResult from './DirectionsResult/DirectionsResult.tsx';
import { measureOverviewPadding } from './helpers/measureOverviewPadding.ts';
import { getOverviewBearing } from './helpers/overviewBearing.ts';
import { hasPreviewableRoute } from './helpers/routePreviewState.ts';
import LanePickerView from './LanePickerView/LanePickerView.tsx';
import {
  checkRoutePath,
  getDirectAlternateRoutes,
  allEndPointsDefined,
  leavingSecurityWarning,
} from './poiListingUtils.ts';
import { getDefaultState, getSelectedSecurityLanes, setDefaultRouteAccessibilityChoice } from './widgetState.ts';

/**
 * Calculates the compass bearing between two navigation endpoints.
 * Used to orient the map when displaying a route.
 *
 * @param {import('./types.ts').NavigationEndpoint[]} endpoints - Array of at least 2 endpoints
 * @returns {number} Bearing in degrees (0-360), or 0 if endpoints are invalid
 *
 * @example
 * const bearing = getBearingBetweenEndpoints([
 *   { lat: 40.7128, lng: -74.0060 },
 *   { lat: 40.7580, lng: -73.9855 }
 * ]);
 * // Returns bearing from first to second point
 */
export const getBearingBetweenEndpoints = endpoints => {
  if (!endpoints || !endpoints[0] || !endpoints[1]) {
    return 0;
  } // point north

  const latlngs = endpoints.flatMap(ep => [ep.lat, ep.lng]);

  return getBearing(...latlngs);
};

/**
 * Main factory function that creates the directions display logic.
 * Registers event handlers, layer widgets, and returns control functions.
 *
 * @param {Object} app - Application context with bus, env, themePack, etc.
 * @param {Object} app.bus - Event bus for inter-plugin communication
 * @param {Object} app.env - Environment detection (isDesktop, isMobile, etc.)
 * @param {Object} app.themePack - UI components from the theme
 * @param {Object} app.log - Logging utility
 * @param {Function} app.gt - Translation function factory
 * @param {Function} app.i18n - Internationalization context
 * @param {Object} config - Plugin configuration
 * @param {import('./widgetState.ts').WidgetState} widgetState - Shared widget state
 * @returns {Object} Object containing exported functions for testing/external use
 */
export default function (app, config, widgetState) {
  const log = app.log.sublog('showDirectionsFromTo');
  const { Loading } = app.themePack;
  let routeRequestId = 0;
  let overviewRequestId = 0;
  let routeSearchInputs = widgetState.getState().searchInputs;

  const beginRouteRequest = () => {
    const requestId = ++routeRequestId;
    ++overviewRequestId;
    routeSearchInputs = widgetState.getState().searchInputs;
    widgetState.update({ isRoutePending: true, hasLoaded: false });
    return requestId;
  };

  const cancelRouteRequest = () => {
    ++routeRequestId;
    ++overviewRequestId;
  };
  const invalidateRoute = () => {
    cancelRouteRequest();
    const exitingGuidance = config.fullRoutePreviewEnabled === true && widgetState.getState().isNavigating;
    widgetState.update({
      isRoutePending: true,
      hasLoaded: true,
      ...(exitingGuidance ? { isNavigating: false, currentStop: 0, currentStep: 0 } : {}),
    });
    if (exitingGuidance) {
      app.bus.send('navigation/guidanceChanged', { active: false, ...getStepPosition() });
    }
  };

  const getStepPosition = () => {
    const { currentStop, currentStep, steps } = widgetState.getState();
    return { stopIndex: currentStop, stepIndex: currentStep, stepCounts: steps.map(leg => leg.length) };
  };
  const recordStep = (kind: 'forward' | 'back' | 'select') => {
    return app.bus.send('navigation/stepChanged', { kind, ...getStepPosition() });
  };

  /**
   * Padding configuration for map animations to avoid UI overlap
   * @type {{top: number, bottom: number, left: number, right: number}}
   */
  const ANIMATION_CENTER_RADIUS = {
    top: 119 + 50, // top widget height + extra margin to fit the start marker
    bottom: 130 + 14, // bottom widget height + extra margin
    left: 20,
    right: 20,
  };

  /**
   * Whole-route overview camera, used in the mobile preview phase when Full Route Preview is enabled.
   * Padding is measured from the on-screen header and Steps | Go bar when the fit runs (header height varies
   * with stop count, logo and collapsed inputs); OVERVIEW_PADDING_FALLBACK is used where nothing can be measured.
   */
  const OVERVIEW_PADDING_FALLBACK = {
    top: 119 + 50,
    bottom: 130 + 14,
    left: 20,
    right: 20,
  };
  /**
   * Space kept clear beyond the measured header / bottom bar: the top leaves room for the end pin (drawn above
   * its point), the bottom for the start marker (a person icon centered on its point).
   */
  const OVERVIEW_PADDING_MARGIN = { top: 50, bottom: 30 };
  /** Lowest zoom the overview may use, so very long routes cannot zoom the map out too far. Must stay above the surround min zoom (12). */
  const OVERVIEW_MIN_ZOOM = 14;
  const OVERVIEW_PITCH = 60;
  /** How long the route accessibility toast stays visible before auto-dismissing. */
  const TOAST_HIDE_DELAY_MS = 4000;

  /** True while Full Route Preview is on and guidance has not started, on any device. */
  const isPreviewPhase = () => config.fullRoutePreviewEnabled === true && !widgetState.getState().isNavigating;

  const isOverviewActive = () => config.fullRoutePreviewEnabled === true && !widgetState.getState().isNavigating;

  // === Event Handlers ===

  /**
   * Handles dynamic routing updates (POIs to avoid)
   */
  app.bus.on('poi/setDynamicRouting', async ({ idValuesMap }) => {
    widgetState.update({ poisToAvoid: Object.values(idValuesMap) });
  });

  /**
   * Main entry point for displaying directions between points.
   * Handles endpoint resolution, route calculation, and display orchestration.
   *
   * @param {import('./types.ts').ShowDirectionsParams} params - Navigation parameters
   *
   * @fires navigation/showDirectionsFromTo
   * @fires searchResults/showPOIs - Clears search results
   * @fires map/cleanMap - Clears previous navigation
   * @fires history/register - Records navigation for back button
   */
  const showDirectionsFromTo = async ({
    endpointIds,
    from,
    to,
    requiresAccessibility,
    doNotMoveMap = false,
    referrer,
    selectedSecurityLanes,
  }) => {
    const requestId = beginRouteRequest();
    // TODO: Figure out what's calling this with single point syntax and convert to multi-point
    if (from && to) {
      endpointIds = [from, to];
    }

    const { endpoints, wereLanesPicked } = widgetState.getState();

    // Use stored preference if not explicitly specified
    if (requiresAccessibility === undefined) {
      requiresAccessibility = widgetState.getState().routeAccessibiltyChoice === RouteType.ACCESSIBLE;
    }

    // Determine route accessibility choice early
    const routeAccessibiltyChoice = requiresAccessibility ? RouteType.ACCESSIBLE : RouteType.DIRECT;
    setDefaultRouteAccessibilityChoice(routeAccessibiltyChoice);

    // Only overwrite referrer if explicitly specified
    if (referrer !== undefined) {
      widgetState.update({ referrer });
    }

    // Clear previous UI state
    app.bus.send('searchResults/showPOIs', { pois: [] });
    app.bus.send('map/cleanMap');

    // Update endpointIds, routeAccessibilityChoice, and selectedSecurityLanes in state BEFORE updating searchInputs to prevent callback re-triggering
    widgetState.update({
      endpointIds,
      routeAccessibiltyChoice,
      selectedSecurityLanes,
    });

    const isDesktop = app.env.isDesktop();

    let navEndpoints;
    try {
      await setSearchInputsFromEndpointsIds(endpointIds, requestId);
      if (requestId !== routeRequestId) {
        return;
      }
      // Resolve all endpoint IDs to full navigation endpoints
      navEndpoints = await Promise.all(
        endpointIds.map(async id => await app.bus.get('wayfinder/getNavigationEndpoint', { ep: id })),
      );
      if (requestId !== routeRequestId) {
        return;
      }

      // Register for browser history navigation
      app.bus.send('history/register', {
        viewId: isDesktop ? DIRECTIONS_RESULT_VIEW_ID_DESKTOP : DIRECTIONS_RESULT_VIEW_ID_MOBILE,
        event: 'navigation/showDirectionsFromTo',
        params: {
          endpointIds,
          requiresAccessibility,
          doNotMoveMap,
          selectedSecurityLanes,
        },
      });
    } catch (error) {
      log.error(error);
      return showUnavailableRouteInfo(requestId);
    }

    if (navEndpoints.length === 0) {
      return showUnavailableRouteInfo(requestId);
    }

    // Detect if this is a refresh of an already-displayed route
    const wasRoutePreviouslyDisplayed = wereLanesPicked && R.equals(endpoints, navEndpoints);

    // Store resolved endpoints to prevent callback re-triggering
    widgetState.update({ endpoints: navEndpoints });

    return displayDirections({
      endpoints: navEndpoints,
      doNotMoveMap,
      wasRoutePreviouslyDisplayed,
      selectedSecurityLanes,
      requestId,
    });
  };

  app.bus.on('navigation/showDirectionsFromTo', showDirectionsFromTo);

  // === Search Input Management ===

  /**
   * Extracts display data for a search input field from an endpoint ID.
   * Handles POI IDs, location objects, and coordinate strings.
   *
   * @param {import('./types.ts').EndpointId} endpointId - Endpoint to process
   * @returns {Promise<import('./types.ts').SearchInput>} Search input data
   * @private
   */
  const getInputNewData = async endpointId => {
    if (!endpointId) {
      return {};
    }

    // Handle location objects (current location, dropped pin)
    if (isLocation(endpointId)) {
      return { location: endpointId, term: endpointId.title };
    }

    // Handle coordinate strings (e.g., "40.7128,-74.0060")
    if (typeof endpointId === 'string' && endpointId.includes(',')) {
      const location = await app.bus.get('wayfinder/getNavigationEndpoint', {
        ep: endpointId,
      });
      return { location, term: location.title };
    }

    // Handle POI IDs
    if (typeof endpointId === 'number' || typeof endpointId === 'string') {
      const poi = await app.bus.get('poi/getById', { id: endpointId });
      if (poi) {
        return { chosenPoi: poi, term: poi.name };
      }
    }

    return {};
  };

  /**
   * Extracts the endpoint ID from a search input object.
   *
   * @param {import('./types.ts').SearchInput} input - Search input to extract from
   * @returns {import('./types.ts').EndpointId|null} Extracted endpoint ID or null
   * @private
   */
  const getEndpointIdFromSearchInput = input => {
    if (!input) {
      return null;
    }
    if (input.chosenPoi?.poiId) {
      return input.chosenPoi.poiId;
    }
    if (input.location) {
      return input.location;
    }
    return null;
  };

  /**
   * Checks if the current searchInputs already represent the given endpointIds.
   * Used to prevent infinite loops when state updates trigger callbacks.
   *
   * @param {import('./types.ts').SearchInput[]} searchInputs - Current search inputs
   * @param {import('./types.ts').EndpointId[]} endpointIds - Endpoint IDs to compare
   * @returns {boolean} True if searchInputs match endpointIds
   * @private
   */
  const searchInputsMatchEndpointIds = (searchInputs, endpointIds) => {
    if (!searchInputs || searchInputs.length !== endpointIds.length) {
      return false;
    }

    return endpointIds.every((endpointId, index) => {
      const currentId = getEndpointIdFromSearchInput(searchInputs[index]);
      if (currentId === null && endpointId === null) {
        return true;
      }
      if (currentId === null || endpointId === null) {
        return false;
      }

      // Compare location objects by their key properties
      if (typeof endpointId === 'object' && typeof currentId === 'object') {
        return endpointId.latitude === currentId.latitude && endpointId.longitude === currentId.longitude;
      }

      return currentId === endpointId;
    });
  };

  /**
   * Updates the search input fields from endpoint IDs.
   * Skips update if inputs already match to prevent infinite loops.
   *
   * @param {import('./types.ts').EndpointId[]} endpointIds - Endpoints to display
   * @returns {Promise<void>}
   */
  const setSearchInputsFromEndpointsIds = async (endpointIds, requestId = routeRequestId) => {
    const { searchInputs: currentSearchInputs } = widgetState.getState();

    // Skip if already matching to prevent infinite loops
    if (searchInputsMatchEndpointIds(currentSearchInputs, endpointIds)) {
      return;
    }

    const updatedSearchInputs = await Promise.all(endpointIds.map(async id => await getInputNewData(id)));

    if (requestId === routeRequestId) {
      routeSearchInputs = updatedSearchInputs;
      widgetState.update({ searchInputs: updatedSearchInputs });
    }
  };

  // === Route Display ===

  /**
   * Orchestrates the display of directions after endpoints are resolved.
   * Sets up state and delegates to displayDirectionsFromToEndpoint.
   *
   * @param {import('./types.ts').DisplayDirectionsParams} params - Display parameters
   * @returns {Promise<void>}
   */
  const displayDirections = async ({
    endpoints,
    doNotMoveMap = false,
    wasRoutePreviouslyDisplayed = false,
    selectedSecurityLanes,
    requestId = beginRouteRequest(),
  }) => {
    if (requestId !== routeRequestId) {
      return;
    }
    widgetState.update({
      wasRoutePreviouslyDisplayed,
      routeTypes: RouteOptionsClassification.DISTINCT,
    });
    await displayDirectionsFromToEndpoint({
      endpoints,
      doNotMoveMap,
      selectedSecurityLanes,
      requestId,
    });
  };

  /**
   * Core route display logic. Checks for security checkpoints, shows lane picker
   * if needed, calculates routes, and displays them.
   *
   * @param {Object} params - Display parameters
   * @param {import('./types.ts').NavigationEndpoint[]} params.endpoints - Resolved endpoints
   * @param {boolean} [params.doNotMoveMap] - Skip map animations
   * @param {import('./types.ts').SelectedSecurityLanes} [params.selectedSecurityLanes] - Pre-selected lanes
   * @returns {Promise<void>}
   * @private
   */
  const displayDirectionsFromToEndpoint = async ({
    endpoints,
    doNotMoveMap,
    selectedSecurityLanes,
    requestId = beginRouteRequest(),
  }) => {
    if (requestId !== routeRequestId) {
      return;
    }
    // Show loading state
    app.bus.send('layers/show', { id: DIRECTIONS_RESULT_CONTROLS_ID });
    app.bus.send('layers/hideMultiple', [
      DIRECTIONS_SEARCH_VIEW_ID,
      DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
      DIRECTIONS_RESULT_VIEW_ID_MOBILE,
    ]);

    try {
      const { routeExists, hasSecurity, hasImmigration } = await checkRoutePath(app.bus, endpoints);
      if (requestId !== routeRequestId) {
        return;
      }
      const leavingSecurity = await leavingSecurityWarning(app.bus, endpoints);
      if (requestId !== routeRequestId) {
        return;
      }
      widgetState.update({ leavingSecurity });

      if (!routeExists) {
        return showUnavailableRouteInfo(requestId);
      }

      if ((hasSecurity || hasImmigration) && !selectedSecurityLanes && !doNotMoveMap) {
        return await displayLanePickerModal(hasSecurity, hasImmigration, requestId);
      }

      const options = selectedSecurityLanes ? { selectedSecurityLanes } : {};
      const { directRoutes, alternativeRoutes } = await getDirectAlternateRoutes(app.bus, endpoints, options);
      if (requestId !== routeRequestId) {
        return;
      }
      if (!directRoutes?.length || directRoutes.some?.(route => !doesRouteExist(route))) {
        return showUnavailableRouteInfo(requestId);
      }
      return displayRoutes(directRoutes, alternativeRoutes, doNotMoveMap);
    } catch (errors) {
      log.error(errors);
      return showUnavailableRouteInfo(requestId);
    }
  };

  // === Lane Picker ===

  /**
   * Marks a single-option lane step as selected, since there is nothing to choose.
   *
   * @param {import('./types.ts').LaneOption[]} lanes - Lane options for a step
   * @returns {import('./types.ts').LaneOption[]} Lanes with the single option pre-selected
   * @private
   */
  const preselectSingleLaneOption = lanes =>
    lanes && lanes.length === 1 ? lanes.map(lane => ({ ...lane, default: true })) : lanes;

  /**
   * Shows the security/immigration lane picker modal.
   * Initializes lane options from venue data if not already loaded.
   *
   * @param {boolean} hasSecurity - Whether route passes security checkpoint
   * @param {boolean} hasImmigration - Whether route passes immigration checkpoint
   * @returns {Promise<void>}
   * @private
   */
  const displayLanePickerModal = async (hasSecurity, hasImmigration, requestId) => {
    const { wasRoutePreviouslyDisplayed } = widgetState.getState();

    // Skip picker if route was already displayed with lane selection
    if (wasRoutePreviouslyDisplayed) {
      return submitLanePickerModal({ requestId });
    }

    let securityLanes = widgetState.getState().securityLanes;
    let immigrationLanes = widgetState.getState().immigrationLanes;

    // Initialize lane options from venue data on first display
    if (!securityLanes && !immigrationLanes) {
      const queueTypes = await app.bus.get('venueData/getQueueTypes');
      if (requestId !== routeRequestId) {
        return;
      }
      securityLanes = hasSecurity ? queueTypes.SecurityLane : [];
      immigrationLanes = hasImmigration ? queueTypes.ImmigrationLane : [];

      if (!securityLanes) {
        throw Error('Unknown queue types for security or immigration');
      }

      widgetState.update({ securityLanes, immigrationLanes });
    }

    // Auto-select single-option steps so there is nothing left to choose.
    securityLanes = preselectSingleLaneOption(securityLanes);
    immigrationLanes = preselectSingleLaneOption(immigrationLanes);
    widgetState.update({ securityLanes, immigrationLanes });

    // If nothing needs choosing, skip the modal and route with the defaults.
    const securityNeedsChoice = (securityLanes?.length ?? 0) > 1;
    const immigrationNeedsChoice = (immigrationLanes?.length ?? 0) > 1;
    if (!securityNeedsChoice && !immigrationNeedsChoice) {
      return submitLanePickerModal({ requestId });
    }

    // Start with security if available, otherwise immigration
    const lanePickerStep = securityLanes.length ? PickerSteps.SECURITY : PickerSteps.IMMIGRATION;
    widgetState.update({ lanePickerStep, wereLanesPicked: false });

    app.bus.send('layers/show', { id: LANE_PICKER_VIEW_ID });
  };

  /**
   * Handles lane picker submission. Collects selected lanes and recalculates route.
   */
  const submitLanePickerModal = async ({ requestId = beginRouteRequest() } = {}) => {
    if (requestId !== routeRequestId) {
      return;
    }
    const { endpoints, securityLanes, immigrationLanes } = widgetState.getState();

    app.bus.send('layers/hide', { id: LANE_PICKER_VIEW_ID });
    widgetState.update({ wereLanesPicked: true });

    // Extract selected lanes from the lane arrays
    const selectedSecurityLanes = getSelectedSecurityLanes(securityLanes, immigrationLanes);

    try {
      const { directRoutes, alternativeRoutes } = await getDirectAlternateRoutes(app.bus, endpoints, {
        selectedSecurityLanes,
      });
      if (requestId !== routeRequestId) {
        return;
      }

      if (directRoutes.length === 0 || directRoutes.some(route => !doesRouteExist(route))) {
        return showUnavailableRouteInfo(requestId);
      }

      return displayRoutes(directRoutes, alternativeRoutes);
    } catch (error) {
      log.error(error);
      return showUnavailableRouteInfo(requestId);
    }
  };

  /**
   * Checks if a route object contains valid steps.
   *
   * @param {import('./types.ts').CalculatedRoute} route - Route to check
   * @returns {number|undefined} Length of steps array, or undefined if invalid
   */
  const doesRouteExist = R.path(['steps', 'length']);

  /**
   * Displays the "route unavailable" error state.
   * Shows the search view and updates state to indicate failure.
   */
  const showUnavailableRouteInfo = async (requestId = routeRequestId) => {
    if (requestId !== routeRequestId) {
      return;
    }
    app.bus.send('navigation/routeUnavailable');
    await app.bus.send('navigation/show', { reset: false });
    if (requestId !== routeRequestId) {
      return;
    }
    app.bus.send('layers/hideMultiple', [DIRECTIONS_RESULT_VIEW_ID_DESKTOP, DIRECTIONS_RESULT_VIEW_ID_MOBILE]);
    widgetState.update({ isRouteUnavailable: true, isRoutePending: false, hasLoaded: true, wereLanesPicked: false });

    // Announce the route-unavailable message to screen readers. This mirrors the two
    // branches rendered by RouteUnavailableOptionsWidget so assistive tech hears the
    // same content that sighted users see. Logic is intentionally duplicated here
    // rather than shared with the widget.
    const T = app.gt();
    const { endpointIds, poisToAvoid } = widgetState.getState();
    const affectedByDynamicRouting = R.intersection(
      poisToAvoid.map(poi => poi.poiId),
      endpointIds,
    );

    let announcementText;
    if (affectedByDynamicRouting.length > 0) {
      const affectedNames = affectedByDynamicRouting.map((poiId, index) => {
        const and = affectedByDynamicRouting.length > 1 && index === affectedByDynamicRouting.length - 1;
        const poiToAvoid = R.find(obj => obj.poiId === poiId)(poisToAvoid);
        return `${and ? ` ${T('getDirectionsFromTo:and')}` : ''} ${poiToAvoid.name}`;
      });
      const detail =
        affectedByDynamicRouting.length > 1
          ? T('getDirectionsFromTo:This route is not possible because _stopNames_ are currently unavailable', {
              stopNames: [affectedNames],
            })
          : T('getDirectionsFromTo:This route is not possible because _stopName_ is currently unavailable', {
              stopName: [affectedNames],
            });
      announcementText = `${T('getDirectionsFromTo:We are sorry')} ${detail}`;
    } else {
      announcementText = `${T('getDirectionsFromTo:Route Unavailable')} ${T(
        'getDirectionsFromTo:Sorry, we couldn’t find a navigation path because the from or to point is not accessible',
      )}`;
    }

    app.bus.send('ariaAnnouncer/announce', {
      announcementKey: 'directionsRouteUnavailable',
      announcementText,
    });
  };

  /**
   * Displays calculated routes on the map and in the step list.
   * Handles both direct and accessible route variants.
   *
   * @param {import('./types.ts').CalculatedRoute[]} directRoutes - Direct routes for each leg
   * @param {import('./types.ts').CalculatedRoute[]|null} accessibleRoutes - Accessible alternatives (may be null)
   * @param {boolean} [doNotMoveMap] - Skip map animations
   */
  const displayRoutes = async (directRoutes, accessibleRoutes, doNotMoveMap) => {
    const { routeAccessibiltyChoice, endpointIds, referrer, isNavigating, currentStop } = widgetState.getState();

    // Show result views
    app.bus.send('layers/show', { id: DIRECTIONS_RESULT_VIEW_ID_DESKTOP });
    app.bus.send('layers/show', { id: DIRECTIONS_RESULT_VIEW_ID_MOBILE });

    const isAccessibleRouteValid = accessibleRoutes?.length > 0 && accessibleRoutes.every?.(Boolean);

    const currentRoute =
      routeAccessibiltyChoice === RouteType.DIRECT || !isAccessibleRouteValid ? directRoutes : accessibleRoutes;
    const { times, distances, steps, segments } = parseRoute(currentRoute);

    // Update state with route data
    widgetState.update({
      times,
      distances,
      steps,
      segments,
      currentStep: 0,
      currentStop: isNavigating ? Math.max(0, Math.min(currentStop, currentRoute.length - 1)) : 0,
      isRoutePending: false,
      hasLoaded: true,
      isRouteUnavailable: false,
      directRoutes,
      accessibleRoutes,
    });

    // Announce first step for screen readers
    app.bus.send('ariaAnnouncer/announce', {
      announcementKey: 'directionsStep',
      announcementText: `${steps[0][0].secondaryText} ${steps[0][0].primaryText}`,
    });

    if (!doNotMoveMap) {
      centerMapForNavline(segments[0]);
    }

    // Hide search view on mobile during navigation
    if (app.env.isMobile()) {
      app.bus.send('layers/hide', { id: DIRECTIONS_SEARCH_VIEW_ID });
    }

    // Determine if primary route is accessible for analytics
    const isPrimaryRouteAccessible =
      routeAccessibiltyChoice === RouteType.ACCESSIBLE ||
      widgetState.getState().routeTypes === RouteOptionsClassification.DEFAULT_IS_ACCESSIBLE;

    // Highlight endpoint POIs on map (exclude bluedot locations)
    const entitiesToHighlight = endpointIds.filter(endpoint => !isLocation(endpoint));
    app.bus.send('map/highlightEntities', { ids: entitiesToHighlight });

    // Classify route options (distinct, default accessible, no accessible)
    const routeTypes = classifyRouteAccessibility(directRoutes, accessibleRoutes);
    widgetState.update({ routeTypes });

    // Send analytics event
    app.bus.send('event/directions', {
      endpointIds,
      routedThroughQueues: directRoutes[0].queues,
      isPrimaryRouteAccessible,
      referrer,
    });
    if (routeTypes === RouteOptionsClassification.NO_ACCESSIBLE_ROUTES) {
      app.bus.send('navigation/noAccessibleRoute', {
        endpointIds,
        accessibleRequested: routeAccessibiltyChoice === RouteType.ACCESSIBLE,
      });
    }

    // Show toast notification about route accessibility
    displayRouteNotification();
  };

  /**
   * Shows a toast notification about the route's accessibility status.
   * Shown on desktop and mobile; auto-dismisses after TOAST_HIDE_DELAY_MS.
   */
  const displayRouteNotification = () => {
    const { routeTypes, routeAccessibiltyChoice, routeTypeAccessibleToggleRef, modalCloseToggleRef } =
      widgetState.getState();
    const T = app.gt();

    if (routeTypes === RouteOptionsClassification.DEFAULT_IS_ACCESSIBLE) {
      app.bus.send('toast/show', {
        message: T('getDirectionsFromTo:Route is accessible'),
        variant: 'info',
        hideDelay: TOAST_HIDE_DELAY_MS,
        placement: 'top',
        focusAfterToastDismissRef: modalCloseToggleRef || null,
      });
    } else if (routeTypes === RouteOptionsClassification.DISTINCT && routeAccessibiltyChoice === RouteType.DIRECT) {
      app.bus.send('toast/show', {
        message: T('getDirectionsFromTo:Accessible route available'),
        variant: 'info',
        hideDelay: TOAST_HIDE_DELAY_MS,
        placement: 'top',
        focusAfterToastDismissRef: routeTypeAccessibleToggleRef || null,
      });
    } else if (routeTypes === RouteOptionsClassification.NO_ACCESSIBLE_ROUTES) {
      app.bus.send('toast/show', {
        message: T('getDirectionsFromTo:No accessible route available'),
        variant: 'info',
        hideDelay: TOAST_HIDE_DELAY_MS,
        placement: 'top',
        focusAfterToastDismissRef: modalCloseToggleRef || null,
      });
    }
  };

  /**
   * Extracts route data arrays from a multi-leg route response.
   *
   * @param {import('./types.ts').CalculatedRoute[]} route - Array of route legs
   * @returns {{times: number[], distances: number[], steps: NavigationStep[][], segments: RouteSegment[][]}}
   */
  const parseRoute = route => {
    const mapLegs = prop => route.map(R.prop(prop));
    return {
      times: mapLegs('time'),
      distances: route.map(getRouteDistance),
      steps: mapLegs('steps'),
      segments: mapLegs('segments'),
    };
  };

  /**
   * Checks if an endpoint is a location object (vs a POI ID).
   *
   * @param {import('./types.ts').EndpointId} endpoint - Endpoint to check
   * @returns {boolean} True if endpoint is a location object
   */
  const isLocation = endpoint => R.keys(endpoint).length;

  const everyStepIsAccessible = route => route.every(leg => leg.steps.every(step => step.isAccessible));

  /**
   * Classifies route options based on accessibility analysis.
   *
   * @param {import('./types.ts').CalculatedRoute[]} directRoute - Direct routes
   * @param {import('./types.ts').CalculatedRoute[]|null} alternativeRoute - Accessible routes
   * @returns {RouteOptionsClassification} The classification of route options
   */
  const classifyRouteAccessibility = (directRoute, alternativeRoute) => {
    if (everyStepIsAccessible(directRoute)) {
      return RouteOptionsClassification.DEFAULT_IS_ACCESSIBLE;
    }

    if (!(alternativeRoute && alternativeRoute.every(x => x) && everyStepIsAccessible(alternativeRoute))) {
      return RouteOptionsClassification.NO_ACCESSIBLE_ROUTES;
    }

    return RouteOptionsClassification.DISTINCT;
  };

  // === Navline Display ===

  let lastNavlineInputs = null;

  /**
   * Renders navigation lines on the map for both direct and accessible routes.
   * Primary route is shown solid, alternative is shown dashed.
   *
   * @param {import('./types.ts').CalculatedRoute[]} directRoutes - Direct routes
   * @param {import('./types.ts').CalculatedRoute[]|null} accessibleRoutes - Accessible routes
   * @param {RouteType} routeAccessibiltyChoice - Which route type is primary
   * @param {number} [indexToFocus=0] - Which leg to highlight as primary
   */
  function displayNavlines(directRoutes, accessibleRoutes, routeAccessibiltyChoice, indexToFocus = 0) {
    app.bus.send('map/resetNavlineFeatures');

    let primaryType = 'multipoint';
    let alternativeType = 'alternativemultipoint';

    directRoutes.forEach((_, index) => {
      // Highlight the focused leg as primary
      if (index === indexToFocus) {
        primaryType = 'primary';
        alternativeType = 'alternative';
      }

      app.bus.send('map/showNavlineFeatures', {
        segments: directRoutes[index].segments,
        category: routeAccessibiltyChoice === RouteType.DIRECT ? primaryType : alternativeType,
      });

      if (accessibleRoutes?.at(index)) {
        app.bus.send('map/showNavlineFeatures', {
          segments: accessibleRoutes[index].segments,
          category: routeAccessibiltyChoice === RouteType.ACCESSIBLE ? primaryType : alternativeType,
        });
      }

      primaryType = 'multipoint';
      alternativeType = 'alternativemultipoint';
    });
  }

  /**
   * Callback registered on widgetState to update navlines when route data changes.
   * Called after every state update (batched via microtask).
   *
   * @returns {Promise<void>}
   * @private
   */
  const showNavlinesAsync = async () => {
    const { directRoutes, accessibleRoutes, currentStop, routeAccessibiltyChoice, searchInputs } =
      widgetState.getState();

    const hasRoutes = directRoutes && directRoutes.length > 0;

    if (!allEndPointsDefined(searchInputs) || !hasRoutes) {
      lastNavlineInputs = null;
      return;
    }

    // Each redraw clears the nav source first, so redrawing unchanged routes makes them blink.
    const inputs = [directRoutes, accessibleRoutes, routeAccessibiltyChoice, currentStop];
    if (lastNavlineInputs?.every((value, i) => value === inputs[i])) {
      return;
    }
    lastNavlineInputs = inputs;
    displayNavlines(directRoutes, accessibleRoutes, routeAccessibiltyChoice, currentStop);
  };

  // cleanMap clears the drawn route, so the next state update must redraw it.
  app.bus.on('map/cleanMap', () => {
    lastNavlineInputs = null;
  });

  widgetState.addCallback(showNavlinesAsync);

  widgetState.addCallback((updatedState, previousState) => {
    const state = widgetState.getState();
    if (
      config.fullRoutePreviewEnabled === true &&
      updatedState.searchInputs !== previousState.searchInputs &&
      state.searchInputs !== routeSearchInputs &&
      state.endpointIds.length > 0 &&
      (state.steps.length > 0 || state.isRoutePending) &&
      !searchInputsMatchEndpointIds(state.searchInputs, state.endpointIds)
    ) {
      invalidateRoute();
    }
  });

  // === Map Animation ===

  /**
   * Centers the map to show the starting portion of the route.
   * Focuses on segments within the starting floor's ordinal.
   *
   * @param {import('./types.ts').RouteSegment[]} segments - First leg's route segments
   * @returns {Promise<void>}
   * @private
   */
  const centerMapForNavline = async segments => {
    if (isOverviewActive()) {
      return centerMapOnWholeRoute();
    }

    // Filter to segments on the starting floor
    const featuresInStartingPointOrdinal = segments.filter(el => el.ordinalId === segments[0].ordinalId);

    // If all other steps are on different floors, animate to first step only
    if (featuresInStartingPointOrdinal.length < 2) {
      return animateToStep(0);
    }

    const { steps, endpoints } = widgetState.getState();

    // Switch to the starting floor
    app.bus.send('mapLevelSelector/selectLevel', {
      id: steps[0][0].animationAnchor.floorId,
    });

    const bounds = findBoundsOfSegments(featuresInStartingPointOrdinal);

    app.bus.send('map/centerInBounds', {
      bounds,
      pitch: 60,
      bearing: getBearingBetweenEndpoints(endpoints),
      animOptions: { padding: ANIMATION_CENTER_RADIUS },
    });
  };

  const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

  /**
   * Reads the overview padding from the live layout. The result views are shown just before the fit runs, so
   * wait (briefly) for the header and bottom bar to mount. Without a map element (e.g. unit tests) there is
   * nothing to measure and the fallback is used immediately.
   *
   * @returns {Promise<import('./helpers/measureOverviewPadding.ts').MapPadding>}
   * @private
   */
  const getOverviewPadding = async () => {
    const mapElement = document.querySelector(app.config?.renderDiv ?? '#renderDiv');
    if (!mapElement) {
      return OVERVIEW_PADDING_FALLBACK;
    }
    // The header and bottom bar publish their own refs (see widgetState); read them after they have mounted.
    const findHeader = () => widgetState.getState().overviewHeaderRef?.current ?? null;
    const findBottomBar = () => {
      const content = widgetState.getState().overviewBottomBarRef?.current ?? null;
      // The container positioning the content also includes the expand handle above it, which covers the map too.
      const container = content?.offsetParent;
      return container instanceof HTMLElement && container !== document.body ? container : content;
    };

    // The views are shown just before the fit runs and mount after a render, so wait two frames. Heights are layout-based, so the slide-in doesn't matter.
    await nextFrame();
    await nextFrame();

    if (app.env.isDesktop()) {
      // The map already has persistent side-panel padding (map/changePadding 'mol'), which MapLibre adds to this.
      return { ...OVERVIEW_PADDING_FALLBACK, top: OVERVIEW_PADDING_MARGIN.top, bottom: OVERVIEW_PADDING_MARGIN.bottom };
    }

    return measureOverviewPadding({
      mapElement,
      headerElement: findHeader(),
      bottomBarElement: findBottomBar(),
      fallback: OVERVIEW_PADDING_FALLBACK,
      margin: OVERVIEW_PADDING_MARGIN,
    });
  };

  /**
   * Frames the whole route (all legs, all floors) so start and end are in view, on the starting floor.
   * Zoom is clamped to OVERVIEW_MIN_ZOOM; when the clamp wins, the route start stays in view.
   * Padding comes from the measured header and bottom bar.
   *
   * @returns {Promise<void>}
   * @private
   */
  const centerMapOnWholeRoute = async () => {
    const requestId = ++overviewRequestId;

    // Switch floors straight away; only the camera move waits for layout.
    const { animationAnchor } = widgetState.getState().steps[0][0];
    app.bus.send('mapLevelSelector/selectLevel', { id: animationAnchor.floorId });

    const padding = await getOverviewPadding();

    // A newer fit (e.g. route-type swap) or Go may have happened while waiting for layout.
    if (requestId !== overviewRequestId || !isOverviewActive()) {
      return;
    }

    const { segments } = widgetState.getState();

    app.bus.send('map/centerInBounds', {
      bounds: findBoundsOfRoute(segments),
      coordinates: segments.flatMap(leg => leg.flatMap(segment => segment.coordinates)),
      pitch: OVERVIEW_PITCH,
      bearing: getOverviewBearing(segments), // start low on screen, route roughly vertical, first leg near-up
      clamp: { minZoom: OVERVIEW_MIN_ZOOM, anchor: { lat: animationAnchor.lat, lng: animationAnchor.lng } },
      animOptions: { padding },
    });
  };

  /**
   * Animates the map to a specific geographic point.
   *
   * @param {number} latitude - Target latitude
   * @param {number} longitude - Target longitude
   * @param {string} floorId - Floor to display
   */
  const animateToPoint = (latitude, longitude, floorId) =>
    app.bus.send('map/animateToPoint', {
      lat: latitude,
      lng: longitude,
      zoom: 19.5,
      floorId,
    });

  /**
   * Animates the map to show a specific navigation step.
   * Announces the step for screen readers.
   *
   * @param {number} stepIndex - Step index within the leg
   * @param {number} [stopIndex=0] - Leg index in multi-point route
   */
  const animateToStep = async (stepIndex, stopIndex = 0) => {
    const { steps } = widgetState.getState();
    const step = steps[stopIndex][stepIndex];

    const T = app.gt();
    const { primaryText, secondaryText, secondaryTextUI, securityWaitTimes } = step;

    let statusText = '';
    if (securityWaitTimes) {
      statusText = securityWaitTimes.isTemporarilyClosed
        ? T('ui:Closed').toUpperCase()
        : T('ui:+xx min', { count: securityWaitTimes.queueTime });
    }

    // Prioritize UI-formatted text for consistency between visual and spoken instructions
    const description = secondaryTextUI || secondaryText;

    const announcementText = `${T('getDirectionsFromTo:Current step is step')} ${
      stepIndex + 1
    }: ${description} ${primaryText} ${statusText}`.trim();

    // Announce for screen readers
    app.bus.send('ariaAnnouncer/announce', {
      announcementKey: 'directionsStep',
      announcementText,
    });

    // First and last steps: zoom to point; middle steps: fit bounds
    if (stepIndex > 0 && stepIndex < steps[stopIndex].length - 1) {
      app.bus.send('map/centerInBounds', {
        bounds: step.bounds,
        floorId: step.animationAnchor.floorId,
        animOptions: { padding: ANIMATION_CENTER_RADIUS },
      });
    } else {
      animateToPoint(step.animationAnchor.lat, step.animationAnchor.lng, step.animationAnchor.floorId);
    }
  };

  // === Step Navigation ===

  /**
   * Advances to the next navigation step.
   * Handles leg transitions in multi-point routes.
   */
  const stepForward = () => {
    const { currentStep, currentStop, steps } = widgetState.getState();
    const newStep = Math.min(currentStep + 1, steps[currentStop].length - 1);

    if (newStep !== currentStep) {
      widgetState.update({ currentStep: newStep });
      recordStep('forward');
      animateToStep(newStep, currentStop);
    } else {
      // At last step of leg - try to advance to next leg
      const newCurrentStop = Math.min(currentStop + 1, steps.length - 1);
      if (newCurrentStop !== currentStop) {
        widgetState.update({ currentStop: currentStop + 1, currentStep: 0 });
        recordStep('forward');
        animateToStep(0, newCurrentStop);
      }
    }
  };

  /**
   * Goes back to the previous navigation step.
   * Handles leg transitions in multi-point routes.
   */
  const stepBackward = () => {
    const { currentStep, currentStop, steps } = widgetState.getState();
    const newStep = Math.max(currentStep - 1, 0);

    if (newStep !== currentStep) {
      widgetState.update({ currentStep: newStep });
      recordStep('back');
      animateToStep(newStep, currentStop);
    } else {
      // At first step of leg - try to go back to previous leg
      const newCurrentStop = Math.max(currentStop - 1, 0);
      if (newCurrentStop !== currentStop) {
        widgetState.update({
          currentStep: steps[newCurrentStop].length - 1,
          currentStop: newCurrentStop,
        });
        recordStep('back');
        animateToStep(steps[newCurrentStop].length - 1, newCurrentStop);
      }
    }
  };

  /**
   * Jumps to a specific step in the navigation.
   *
   * @param {Object} params - Step location
   * @param {number} params.stopIndex - Leg index
   * @param {number} params.stepIndex - Step index within leg
   */
  const stepTo = ({ stopIndex, stepIndex }) => {
    widgetState.update({ currentStop: stopIndex, currentStep: stepIndex });
    recordStep('select');
    animateToStep(stepIndex, stopIndex);
  };

  // === UI Controls ===

  /**
   * Enters guidance from the route preview. Ignored unless a valid route is being previewed,
   * so a repeated activation or stale render cannot toggle guidance back off.
   */
  const startGuidance = () => {
    if (!isPreviewPhase() || !hasPreviewableRoute(widgetState.getState())) {
      return;
    }
    return toggleIsNavigating();
  };

  const endGuidance = () => {
    const state = widgetState.getState();
    if (config.fullRoutePreviewEnabled !== true || !state.isNavigating || !hasPreviewableRoute(state)) {
      return;
    }
    widgetState.update({ isNavigating: false });
    toggleNavigationHeader(true);
    app.bus.send('navigation/guidanceChanged', { active: false, ...getStepPosition() });
    return centerMapOnWholeRoute();
  };

  /**
   * Toggles between expanded and collapsed navigation view.
   */
  const toggleIsNavigating = () => {
    // Evaluate before flipping: the preview only applies while still in the preview phase.
    const startingGuidanceFromPreview = isPreviewPhase();
    const isNavigating = widgetState.getState().isNavigating;
    widgetState.update({ isNavigating: !isNavigating });
    if (startingGuidanceFromPreview) {
      // The preview frames the whole route; Start zooms in to the first step.
      widgetState.update({ currentStop: 0, currentStep: 0 });
      animateToStep(0, 0);
    }
    return app.bus.send('navigation/guidanceChanged', { active: !isNavigating, ...getStepPosition() });
  };

  /**
   * Shows or hides the navigation header.
   *
   * @param {boolean} toggleState - Whether to show the header
   */
  const toggleNavigationHeader = toggleState => {
    app.bus.send('screen/displayHeader', { display: toggleState });
  };

  /**
   * Handles "Edit Route" click - returns to search view.
   */
  const onEditRouteClick = () => {
    app.bus.send('navigation/editStops');
    app.bus.send('layers/hideMultiple', [DIRECTIONS_RESULT_VIEW_ID_DESKTOP, DIRECTIONS_RESULT_VIEW_ID_MOBILE]);
    app.bus.send('navigation/show', { reset: false });
  };

  /**
   * Handles "End Route" click - resets state and returns to default view.
   * Preserves security/immigration lane preferences for future routes.
   */
  const onEndRouteClick = () => {
    return app.bus.send('navigation/closeDirections', { reason: 'endRoute' });
  };

  app.bus.on('navigation/closeDirections', ({ reason }) => {
    cancelRouteRequest();
    if (reason === 'endRoute') {
      app.bus.send('headerOnline/show');
      app.bus.send('layers/hideMultiple', [DIRECTIONS_RESULT_VIEW_ID_DESKTOP, DIRECTIONS_RESULT_VIEW_ID_MOBILE]);
      widgetState.update(getDefaultState(widgetState));
    }
  });
  app.bus.on('app/reset', cancelRouteRequest);
  app.bus.on('navigation/editStops', invalidateRoute);

  /**
   * Handles lane picker dismiss - applies default "general" lane selection.
   */
  const handleModalDismissWithDefaultOption = () => {
    const currentState = widgetState.getState();

    const setDefaultOption = lanes => {
      return lanes.map(lane => ({ ...lane, default: lane.id === 'general' }));
    };

    widgetState.update({
      ...currentState,
      securityLanes: setDefaultOption(currentState.securityLanes),
      immigrationLanes: setDefaultOption(currentState.immigrationLanes),
    });

    submitLanePickerModal();
  };

  // === Layer Registration ===

  const locale = app.i18n().language;

  app.bus.send('layers/register', {
    id: DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
    widget: () => (
      <DirectionsResult
        bus={app.bus}
        onNextStepClick={stepForward}
        onPrevStepClick={stepBackward}
        onStepClickHandler={stepTo}
        toggleIsNavigating={toggleIsNavigating}
        toggleNavigationHeader={toggleNavigationHeader}
        widgetState={widgetState}
        isDesktop={() => app.env.isDesktop()}
        Loading={Loading}
        T={app.gt()}
        onEditRouteClick={onEditRouteClick}
        onEndRouteClick={onEndRouteClick}
        locale={locale}
        fullRoutePreviewEnabled={config.fullRoutePreviewEnabled === true}
        onStartRoute={startGuidance}
        onEndNavigationClick={endGuidance}
      />
    ),
    layoutId: 'content',
    layoutName: 'fullscreen',
    widgetType: widgetEnum.Desktop,
  });

  app.bus.send('layers/register', {
    id: DIRECTIONS_RESULT_VIEW_ID_MOBILE,
    widget: () => (
      <DirectionsResult
        bus={app.bus}
        onNextStepClick={stepForward}
        onPrevStepClick={stepBackward}
        onStepClickHandler={stepTo}
        toggleIsNavigating={toggleIsNavigating}
        toggleNavigationHeader={toggleNavigationHeader}
        widgetState={widgetState}
        isDesktop={app.env.isDesktop}
        Loading={Loading}
        T={app.gt()}
        onEditRouteClick={onEditRouteClick}
        onEndRouteClick={onEndRouteClick}
        locale={locale}
        isVeryNarrow={app.env.isVeryNarrow()}
        fullRoutePreviewEnabled={config.fullRoutePreviewEnabled === true}
        onStartRoute={startGuidance}
        onEndNavigationClick={endGuidance}
      />
    ),
    layoutId: 'bottomBar',
    layoutName: 'fullscreen',
    widgetType: widgetEnum.Mobile,
  });

  app.bus.send('layers/register', {
    id: LANE_PICKER_VIEW_ID,
    widget: () => (
      <LanePickerView
        widgetState={widgetState}
        isDesktop={() => app.env.isDesktop()}
        T={app.gt()}
        onSubmitClick={submitLanePickerModal}
        onModalDismiss={handleModalDismissWithDefaultOption}
        highZoomMaxWidth={app.config.mobileHighZoomBreakpoint}
      />
    ),
    layoutId: 'modalWindow',
    layoutName: 'fullscreen',
  });

  return {
    ANIMATION_CENTER_RADIUS,
    animateToPoint,
    classifyRouteAccessibility,
    displayDirections,
    displayNavlines,
    displayRoutes,
    endGuidance,
    getInputNewData,
    parseRoute,
    setSearchInputsFromEndpointsIds,
    showUnavailableRouteInfo,
    stepBackward,
    stepForward,
    startGuidance,
    stepTo,
    submitLanePickerModal,
    toggleIsNavigating,
  };
}
